import React, { useCallback, useState } from 'react';
import type { MyProductReview } from '../../../../../music24-api/src/productReviews/productReviewTypes';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useMyReview } from './useMyReview';
import { ReviewForm } from './ReviewForm';
import { ReviewThanks } from './ReviewThanks';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the review screen; each part uses its own slice of it */
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The states of the review page: loading (a3), load error (a4), the form (a1, a2, a5, a7-a9) and
 * the thank-you state after a send (a6). "Edit my review" opens the form with the saved review.
 */
const ReviewScreenComponent: React.FC = () => {
  const { answer, isLoading, error, refetch } = useMyReview();
  const [saved, setSaved] = useState<MyProductReview | null>(null);
  const [editing, setEditing] = useState(false);

  const onSaved = useCallback((review: MyProductReview) => {
    setSaved(review);
    setEditing(false);
  }, []);
  const onEdit = useCallback(() => setEditing(true), []);

  const content = () => {
    if (isLoading) {
      return <LoadingView />;
    }
    if (error !== undefined || answer === undefined) {
      return <ErrorView error={error as Error | undefined} retry={refetch} />;
    }
    if (saved !== null && !editing) {
      return <ReviewThanks onEdit={onEdit} />;
    }
    const current = saved !== null ? { ...answer, review: saved } : answer;
    // A new key after a save makes the form start from the saved values.
    return <ReviewForm key={current.review?.updatedAt ?? 'new'} answer={current} onSaved={onSaved} />;
  };

  return <div className={styles.page}>{content()}</div>;
};

export const ReviewScreen = TypedMemo(ReviewScreenComponent);
